import React, { useEffect, useState, useRef } from 'react';
import useInView from '../../../hooks/useInView';
import { useDispatch, useSelector } from 'react-redux';
import { setEntrancesInView } from '../../../store/actions/housingStockActions';
import { getNumOfCol, getPreparedFlats } from './utils';
import FloorTooltip from './components/FloorTooltip/FloorTooltip';
import GridContainer from './components/GridContainer/GridContainer';
import { gridContainerTypes } from './constants';

const initialCustomTooltipData = {
  visible: false,
  content: '',
  height: '',
  top: '',
  left: 0,
};

const FlatsGrid = ({
  data,
  className,
  setCurrentEntry,
  colSize = '46px',
  rowSize = '46px',
  onFlatClick,
  parentContainer,
  skip = false,
  threshold = 0.9,
  rootMargin = '100% 0px 100% 0px',
  isPreview = false,
}) => {
  const dispatch = useDispatch();

  const ref = useRef();

  const { entrancesInView, activeEntranceName } = useSelector(
    (state) => state.housingStockStore
  );

  const [upperRooms, setUpperRooms] = useState([]);
  const [basementRooms, setBasementRooms] = useState([]);
  const [undergroundRooms, setUndergroundRooms] = useState([]);
  const [customTooltipData, setCustomTooltipData] = useState(
    initialCustomTooltipData
  );
  const [numOfCol, setNumOfCol] = useState(1);

  const handleMouseOver = (e) => {
    const target = e.target;

    if (target.hasAttribute('data-floor')) {
      const data = {
        visible: true,
        content: target.getAttribute('data-floor'),
        height: target.offsetHeight + 8,
        top: target.offsetTop - 4,
        left: ref.current.getBoundingClientRect().left,
      };

      setCustomTooltipData(data);
    }
  };

  const handleMouseOut = (e) => {
    setCustomTooltipData(initialCustomTooltipData);
  };

  const {
    ref: inViewRef,
    inView,
    entry,
  } = useInView({
    root: parentContainer?.current,
    threshold,
    rootMargin,
    skip,
  });

  // set preparedFlats for grid
  useEffect(() => {
    let upper = [];
    let basement = [];
    let under = [];
    if (data?.flats) {
      const { upperRooms, basementRooms, undergroundRooms } = getPreparedFlats(
        data.flats
      );

      upper = upperRooms;
      basement = basementRooms;
      under = undergroundRooms;
    }

    setUpperRooms(upper);
    setBasementRooms(basement);
    setUndergroundRooms(under);
  }, [data]);

  // set entrances in view
  useEffect(() => {
    let newEntrancesInView;

    if (inView) {
      newEntrancesInView = [...entrancesInView, data.name];
    } else {
      newEntrancesInView = entrancesInView.filter(
        (entranceId) => entranceId !== data.name
      );
    }

    dispatch(setEntrancesInView(newEntrancesInView));
  }, [inView, dispatch, data.name]);

  // set active entrance tab
  useEffect(() => {
    if (setCurrentEntry && activeEntranceName === data.name && entry) {
      setCurrentEntry(entry.target);
    }
  }, [activeEntranceName, data.name, entry, setCurrentEntry]);

  // set number of columns for root
  useEffect(() => {
    let colCount = 1;

    if (data.flats?.length > 0) {
      colCount = getNumOfCol(data.flats);
    }

    setNumOfCol(colCount);
  }, [data.flats]);

  return (
    <div ref={inViewRef} className={className}>
      <div ref={ref} style={{ position: 'relative' }}>
        {customTooltipData.visible && <FloorTooltip data={customTooltipData} />}
        {Boolean(upperRooms.length) && (
          <GridContainer
            onMouseOver={!isPreview ? handleMouseOver : undefined}
            onMouseOut={!isPreview ? handleMouseOut : undefined}
            rowSize={rowSize}
            numOfCol={numOfCol}
            flats={upperRooms}
            onFlatClick={onFlatClick}
            isPreview={isPreview}
            colSize={colSize}
            title={data.name}
          />
        )}
        {Boolean(basementRooms.length) && (
          <GridContainer
            onMouseOver={!isPreview ? handleMouseOver : undefined}
            onMouseOut={!isPreview ? handleMouseOut : undefined}
            rowSize={rowSize}
            numOfCol={numOfCol}
            flats={basementRooms}
            onFlatClick={onFlatClick}
            isPreview={isPreview}
            colSize={colSize}
            type={gridContainerTypes.basement}
          />
        )}
        {Boolean(undergroundRooms.length) && (
          <GridContainer
            onMouseOver={!isPreview ? handleMouseOver : undefined}
            onMouseOut={!isPreview ? handleMouseOut : undefined}
            rowSize={rowSize}
            numOfCol={numOfCol}
            flats={undergroundRooms}
            onFlatClick={onFlatClick}
            isPreview={isPreview}
            colSize={colSize}
            type={gridContainerTypes.under}
          />
        )}
      </div>
    </div>
  );
};

export default FlatsGrid;
